import { Grid, Link as TableLink, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { SimpleDialog } from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import MainPane from 'src/components/TwoPaneLayout/MainPane';
import SidePane from 'src/components/TwoPaneLayout/SidePane';
import TwoPaneLayout from 'src/components/TwoPaneLayout/TwoPaneLayout';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useModal } from 'src/hooks';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import { IFilter, ILocation } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { LocationList } from '../listLocationTagsPage/modules/locationList/LocationList';
import {
  InputContainer,
  InputTextField,
  StyleDivider,
} from './AddLocationTagPage.styled';
import { tagNameMaxLength, TestIds } from './constants';
import { useCreateLocationTagPage } from './hooks/AddLocationTagPage.hook';

export const AddLocationTagPage = () => {
  const [inputBoxStatus, setInputBoxStatus] = useState('');
  const [locationTagName, setLocationTagName] = useState('');

  const [locations, setLocations] = useState<ILocation[]>([]);

  const [locationsChanged, setLocationsChanged] = useState<boolean>(false);

  const [viewLocations, setViewLocations] = useState<ILocation[]>([]);

  const [locationsSelectedCount, setLocationsSelectedCount] =
    useState<number>(0);
  const {
    createLocationTag,
    updateLocations,
    totalPages,
    totalCount,
    page,
    locationsBase,
    isLoadingLocations,
    handlePageChange,
    getParamsValue,
    activeFilters,
  } = useCreateLocationTagPage();
  const navigate = useNavigate();
  const CloseModal = useModal();
  const { isViewOnly } = useUserRole();
  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const [isStoreNumberFuzzySearch, setIsStoreNumberFuzzySearch] = useState(
    !('store_number.list' in activeFilters),
  );

  const onStoreNumberSearchTypeChange = () => {
    if (isStoreNumberFuzzySearch) {
      setIsStoreNumberFuzzySearch(false);
      onChangeToExactMatch('store_number.regexp', 'store_number.list');
    } else {
      setIsStoreNumberFuzzySearch(true);
      onChangeToFuzzyMatch('store_number.list', 'store_number.regexp');
    }
  };
  const { handleOnChangeFilterKeyword, handleOnRemoveFilterKeyword } =
    useSearchFilter();
  const filters: IFilter[] = [
    {
      label: lang.locationTagsName,
      labelOverride: lang.locationTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
      customElement: (
        <TableLink
          component="button"
          variant="body2"
          onClick={() => navigate(PATHS.LOCATIONS_TAGS)}>
          {lang.manageTags}
        </TableLink>
      ),
    },
    {
      labelOverride: lang.ownerOperator.ownerOperatorTags,
      label: lang.locationOwnerTagsName,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      label: lang.zone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      label: lang.subZone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      labelOverride: 'Store number',
      label: 'store_number',
      isLoading: false,
      options: [],
      fieldType: 'freeSearch',
      hasExactSearch: true,
      isFuzzySearch: isStoreNumberFuzzySearch,
      onSearchTypeChange: onStoreNumberSearchTypeChange,
      filterSearchVal: getParamsValue(
        isStoreNumberFuzzySearch ? 'store_number.regexp' : 'store_number.list',
      ),
      onChangeFilterKeyword: (value: string) =>
        handleOnChangeFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
      onRemoveFilterKeyword: (value: string) =>
        handleOnRemoveFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
    },
  ];
  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onChangeParams,
    onReset,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onResetFilters = () => {
    onReset();
    onChangeParams('store_number.list', []);
    onChangeParams('store_number.regexp', []);
    setIsStoreNumberFuzzySearch(true);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const handleCategoryNameChnage = (value: string) => {
    const reg = new RegExp('^[a-zA-Z0-9- ]*$');
    reg.test(value)
      ? setInputBoxStatus('')
      : setInputBoxStatus('Invalid tag name');
    setLocationTagName(value);
  };

  const handleModalConfirm = () => {
    navigate(PATHS.LOCATIONS_TAGS);
  };

  useEffect(() => {
    const newViewLocations = locationsBase.map((locationBase) => {
      const changedLocation = locations.find(
        (location) => location.location_code === locationBase.location_code,
      );

      return changedLocation ?? locationBase;
    });

    setViewLocations(newViewLocations);
  }, [locationsBase, locations]);

  const handleUpdateLocations = (
    newLocations: ILocation[],
    isDeselectAll: boolean = false,
  ) => {
    const updatedMap = new Map(
      locations.map((loc) => [loc.location_code, loc]),
    );

    for (const newLoc of newLocations) {
      updatedMap.set(newLoc.location_code, newLoc);
    }

    let updatedLocationArray = Array.from(updatedMap.values());

    if (isDeselectAll) {
      updatedLocationArray = updatedLocationArray.map((loc) => ({
        ...loc,
        location_tag_id: [],
      }));
    }

    setLocations([...updatedLocationArray]);

    if (!locationsChanged) setLocationsChanged(true);
  };

  const handleDone = async () => {
    try {
      if (locationTagName) {
        const request = {
          name: locationTagName,
        };
        const result = await createLocationTag.mutateAsync(request);

        if (result.id) {
          const updatedLocations = locations
            .filter((location) => location.location_tag_id?.includes(-1))
            .map((location) => ({
              ...location,
              location_tag_id: [
                ...(location.location_tag_id?.filter((id) => id !== -1) ?? []),
                result.id,
              ],
            }));

          await updateLocations.mutateAsync(updatedLocations);
          navigate(PATHS.LOCATIONS_TAGS);
          Snackbar.success(`${locationTagName} ${lang.successfullyCreated}`);
        }
      }
    } catch (error: any) {}
  };

  return (
    <Grid>
      <Header
        title={lang.createNewTag}
        breadcrumbItems={[
          {
            label: lang.locations,
            link: `${PATHS.LOCATIONS}`,
          },
          {
            label: lang.tags,
            link: `${PATHS.LOCATIONS_TAGS}`,
          },
          {
            label: lang.createTag,
          },
        ]}
        actionBar={isViewOnly && <ViewOnlyAlert />}
      />
      <TwoPaneLayout>
        <SidePane>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeFilter}
            showReset
            onReset={onResetFilters}
            hasExactSearch
            onSearchTypeChange={onSearchTypeChange}
            isFuzzySearch={isFuzzySearch}
          />
        </SidePane>
        <MainPane>
          <Typography variant="h4" component="h2" gutterBottom>
            {lang.createTag}
          </Typography>
          <StyleDivider />
          <InputContainer>
            <InputTextLabel>{lang.tagName}</InputTextLabel>
            <InputTextField
              variant="outlined"
              inputProps={{
                maxLength: tagNameMaxLength,
                'data-testid': TestIds.TAG_NAME_INPUT,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleCategoryNameChnage(event.target.value)
              }
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleCategoryNameChnage(event.target.value.trim())
              }
              value={locationTagName}
              error={!!inputBoxStatus}
              helperText={inputBoxStatus}
            />
          </InputContainer>
          <LocationList
            locations={viewLocations}
            isLoadingLocations={isLoadingLocations}
            totalPages={totalPages}
            totalCount={totalCount}
            page={page}
            tagId={-1}
            handleUpdateLocations={handleUpdateLocations}
            handlePageChange={handlePageChange}
            locationsSelectedCount={locationsSelectedCount}
            setLocationsSelectedCount={setLocationsSelectedCount}
          />
        </MainPane>
      </TwoPaneLayout>
      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
      {!isViewOnly && (
        <Footer
          showFooterActions
          onCancel={CloseModal.onOpenModal}
          onSave={handleDone}
          cancelLabel={lang.close}
          saveLabel={lang.create}
          disableSave={locationTagName === '' || inputBoxStatus !== ''}
          isSaving={createLocationTag.isLoading || updateLocations.isLoading}
        />
      )}
    </Grid>
  );
};
